/// <reference types="@types/google.maps" />
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { BREAKPOINTS, CLASSES, SELECTORS, STORAGE_NAMES } from '@root/app.config';
import {
	Breakpoints,
	ContactForm,
	FarSettings,
	GoogleMapsInfoWindow,
	ProductType,
	Retailer,
	RetailerClickActions,
} from '@root/app.interfaces';

import { ServerDataService } from '@core/services/server-data/server-data.service';
import { catchError, EMPTY, finalize, of, Subscription, switchMap } from 'rxjs';

import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { RetailerSuccessModalComponent } from '@shared/modals/retailer-success-modal/retailer-success-modal.component';
import { RetailerNoSelectModalComponent } from '@shared/modals/retailer-no-select-modal/retailer-no-select-modal.component';
import { RetailerSelectModalComponent } from '@shared/modals/retailer-select-modal/retailer-select-modal.component';
import { ContactFormComponent } from '@features/contact-form/contact-form.component';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { ModalService } from '@core/services/modal/modal.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_OVERVIEW_EVENTS } from '@core/services/gtm/gtm.events';
import { EnquiryErrorModalComponent } from '@shared/modals/enquiry-error-modal/enquiry-error-modal.component';
import { OverviewReportingService } from '@features/overview-page/services/overview-reporting/overview-reporting.service';
import { ActionTrackingService } from '@core/services/action-tracking/action-tracking.service';

declare const $: any;

@Component({
	selector: 'app-retailer-action',
	templateUrl: './retailer-action.component.html',
	styleUrls: [
		'./retailer-action.component.scss',
		'./retailer-action.responsive.scss',
		'../overview-page/overview-page.component.scss',
		'../overview-page/overview-page.responsive.scss',
	],
	encapsulation: ViewEncapsulation.None,
})
export class RetailerActionComponent implements OnInit, AfterViewInit, OnDestroy {
	@Output() toggleInfoAvailability = new EventEmitter();

	@ViewChild('mapElement') mapElement: ElementRef;
	@ViewChild('retailerNumber') retailerNumber: ElementRef;
	@ViewChild('retailerContact') retailerContact: ElementRef;
	@ViewChild('retailerSendData') retailerSendData: ElementRef;
	@ViewChild('childRef') contactFormRef: ContactFormComponent;

	getServerData: Subscription;
	getSettingsData: Subscription;
	getAccess: Subscription;
	getUserTime: Subscription;

	farSettings: { hasData: boolean; data: FarSettings } = {
		hasData: false,
		data: null,
	};

	isMobile: boolean;
	shown = false;
	loading = false;
	searchRetailerAvailability = false;
	contactRetailerAvailability = false;
	embeddedRetailer = null;

	far = {
		searchMarker: null,
		geocoder: null,
		access: 0,
		search_count: 0,
		map: null,
		markers: [],
		retailers: [],
		isMobile: this.countryCodeService.mobileAndTabletCheck(),
		numberOfSelectedRetailers: 0,
		premiumMarkers: [] as Retailer[],
		nonPremiumMarkers: [] as Retailer[],
		checkedRetailersID: [],
		center: [],
		formData: {},
		hasResults: false,
	};

	contactForm: ContactForm;
	contactFormLoading = false;

	infoWindows: any[] = [];
	mapSelectedRetailer: Retailer | null;
	breakpoints: Breakpoints = BREAKPOINTS;
	checkedRetailers: Retailer[] = [];
	productType: ProductType | 'all';

	static onConsoleResponse(message: string): void {
		return console.warn(message);
	}

	static Retailer(retailer: Retailer) {
		return {
			id: retailer['id'],
			logo: retailer['logo'],
			name: retailer['name'],
			address: retailer['address'],
			suburb: retailer['suburb'],
			state: retailer['state'],
			zip_code: retailer['zip_code'],
			phone: retailer['phone'],
			product: retailer['product'],
			icon: retailer['icon'],
			latitude: retailer['latitude'],
			longitude: retailer['longitude'],
			store_type: retailer['store_type'],
			car_button: retailer['car_button'],
			premium: retailer['premium'],
			swiftee: retailer['swiftee'],
			service_and_repairs: retailer['service_and_repairs'],
			display_count: retailer['display_count'],
			display_coeff: retailer['display_coeff'],
			boost: +retailer['display_count'] / +retailer['display_coeff'],
			full_address: retailer['full_address'],

			marker: new google.maps.Marker({
				position: new google.maps.LatLng(+retailer['latitude'], +retailer['longitude']),
				title: retailer[`name`],
				optimized: true,
				icon: !!+retailer[`premium`] ? 'assets/icons/new/map-marker-premium-new.svg' : 'assets/icons/new/map-marker-new.svg',
			}),
		};
	}

	static deleteMarkers(markers): void {
		markers.forEach((marker) => {
			marker.marker.setMap(null);
		});
	}

	static getDistance(markers, center): any {
		for (const marker of markers) {
			marker.distance = google.maps.geometry.spherical.computeDistanceBetween(center, marker.marker.getPosition());
		}

		return markers;
	}

	static sortByDistance(a, b): any {
		return a.distance - b.distance;
	}

	static deleteMarkersOutsideCircle(circle, markers): any {
		const filteredMarkers = [];
		for (const marker of markers) {
			if (marker.distance < circle.radius) {
				filteredMarkers.push(marker);
			}
		}
		return filteredMarkers;
	}

	@HostListener('window:resize', ['$event']) fitRetailerCardHeight(): void {
		const cards = Array.from(document.querySelectorAll(SELECTORS.retailer_card_header)) as HTMLElement[];

		if (!cards.length) {
			return;
		}

		const rows: HTMLElement[][] = [[]];

		cards.forEach((card, index) => {
			if (index > 0 && card.getBoundingClientRect().top !== cards[index - 1].getBoundingClientRect().top) {
				rows.push([]);
			}
			rows[rows.length - 1].push(card);
		});

		rows.forEach((row) => {
			const heights = row.map((card) => {
				card.style.minHeight = 'unset';
				return card.offsetHeight;
			});

			row.forEach((card) => (card.style.minHeight = `${Math.max(...heights)}px`));
		});
	}

	get retailerSearchTranslationKey(): string {
		const count = this.farSettings.data?.number_of_retailers_can_be_selected;
		return count > 1 ? 'retailer_action.blocks.search.text' : 'retailer_action.blocks.search.text_single';
	}

	constructor(
		private serverDataService: ServerDataService,
		private shareServiceData: ShareService,
		private changeDetection: ChangeDetectorRef,
		private sessionStorageService: SessionStorageService,
		private countryCodeService: SetupPageService,
		private saveSessionBlindService: SaveSessionBlindsService,
		private modalService: ModalService,
		private overviewReportingService: OverviewReportingService,
		public dialog: MatDialog,
		public zone: NgZone,
		private gtm: GtmService,
		private actionTrackingService: ActionTrackingService,
	) {}

	ngOnInit(): void {
		this.getRetailersData();
		this.isMobile = this.countryCodeService.mobileAndTabletCheck();
	}

	ngOnDestroy(): void {
		this.getSettingsData?.unsubscribe();
		this.getServerData?.unsubscribe();
		this.getAccess?.unsubscribe();
		this.getUserTime?.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.far.geocoder = new google.maps.Geocoder();
		this.far.map = new google.maps.Map(this.mapElement.nativeElement, {
			zoom: 4,
			center: new google.maps.LatLng(-26.4390917, 133.281323),
		});
	}

	getRetailersData(): void {
		this.embeddedRetailer = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer);

		if (!this.embeddedRetailer) {
			this.getServerData = this.serverDataService.GetRetailersData().subscribe((res) => {
				this.far.retailers = res;
				this.changeDetection.markForCheck();
			});
		} else {
			setTimeout(() => {
				this.far.retailers = [this.embeddedRetailer];
				this.far.checkedRetailersID = [this.embeddedRetailer.id];
				this.checkedRetailers = [this.embeddedRetailer];
				this.far.numberOfSelectedRetailers = 1;

				if (this.embeddedRetailer) {
					this.checkAccessAndProcessSearch();
				}

				$(SELECTORS.overview_add_block)[0].scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				});
			});
		}

		this.getSettingsData = this.serverDataService.GetSettingsData().subscribe((res) => {
			this.farSettings.data = res;
			this.farSettings.hasData = true;

			this.changeDetection.markForCheck();
		});
	}

	checkAccessAndProcessSearch(): void {
		this.getSettingsData = this.serverDataService.GetAccess().subscribe((res) => {
			this.far.access = res.access;
			this.far.search_count = res.search_count;

			if (res.access) {
				this.geocodeAddress(this.contactForm?.post_code, this.far.geocoder, this.far.map);
			} else {
				this.dialog.open(SearchLimitModalComponent);
				this.sendReporting();
			}
			this.contactFormLoading = false;
			this.changeDetection.markForCheck();
		});
	}

	geocodeAddress(addressCode, geoCoder, resultsMap): void {
		const countryData = this.countryCodeService.getCurrentCountry();

		const postCodeRegex = countryData.code !== 'sg' ? /^[0-9]{4}$/ : /^[0-9]{6}$/;
		if (postCodeRegex.test(addressCode)) {
			addressCode = 'Postcode: ' + addressCode + ' ' + countryData?.name;
		}

		geoCoder.geocode(
			{
				address: addressCode,
				componentRestrictions: { country: countryData?.code },
			},
			(results, status) => {
				if (status === google.maps.GeocoderStatus.OK) {
					resultsMap.setCenter(results[0].geometry.location);
					if (this.far.searchMarker) {
						this.far.searchMarker.setPosition(results[0].geometry.location);
					} else {
						this.far.searchMarker = new google.maps.Marker({
							map: resultsMap,
							position: results[0].geometry.location,
						});
					}

					this.findClosestMarkers(results[0]);
					this.mapMarkersHelper();
				}

				if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
					this.modalService.openResponsiveDialog({
						component: RetailerNoSelectModalComponent,
						minHeight: '200px',
						maxWidth: '680px',
						width: '100%',
						height: 'auto',
					});

					this.searchRetailerAvailability = false;
				}
			},
		);
	}

	getMarkers(): any {
		const markers = [];
		if (Array.isArray(this.far.markers) && this.far.markers.length) {
			return this.far.markers;
		} else {
			for (const item of this.far.retailers) {
				const retailer = RetailerActionComponent.Retailer(item);
				markers.push(retailer);
			}
		}

		return markers;
	}

	scrollIntoView(): void {
		setTimeout(() => {
			this.retailerContact.nativeElement.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		});
	}

	setMarkers(map, markers): void {
		markers.forEach((marker) => {
			marker.marker.setMap(map);
		});
	}

	zoomMap(map, markers): void {
		const bounds = new google.maps.LatLngBounds();
		markers.forEach((marker) => {
			bounds.extend(marker.marker.getPosition());
		});

		bounds.extend(this.far.searchMarker.getPosition());
		map.fitBounds(bounds);
	}

	updateRetailers(): void {
		const nonPremiumRetailersId = [];
		const nonPremiumRetailersCounts = [];

		for (const nonPremiumMarker of this.far.nonPremiumMarkers) {
			nonPremiumRetailersId.push(nonPremiumMarker.id);
			nonPremiumRetailersCounts.push(nonPremiumMarker.display_count);

			const index = this.far.markers.findIndex((marker) => marker.id === nonPremiumMarker.id);

			if (index) {
				this.far.markers[index].display_count++;
				this.far.markers[index].boost = this.far.markers[index].display_count / this.far.markers[index].display_coeff;
			}
		}
	}

	createCircle(radius, centerPoint): any {
		return new google.maps.Circle({
			center: centerPoint,
			map: this.far.map,
			radius: radius * 1000,
			visible: false,
		});
	}

	getClosestPremiumMarkers(allPremiumMarkers, centerLocation): any {
		const premiumCircle = this.createCircle(this.farSettings.data.max_premium_distance, centerLocation);

		let premiumMarkers = RetailerActionComponent.deleteMarkersOutsideCircle(premiumCircle, allPremiumMarkers);

		premiumMarkers = this.filterByProduct(premiumMarkers);
		premiumMarkers.sort(RetailerActionComponent.sortByDistance);
		premiumMarkers = premiumMarkers.splice(0, this.farSettings.data.premium_number);

		return premiumMarkers;
	}

	getClosestNonPremiumMarkers(allNonPremiumMarkers, centerLocation): any {
		if (+this.farSettings.data.show_premium_retailers_only && this.far.premiumMarkers.length) {
			return [];
		}

		let nonPremiumCircle = this.createCircle(this.farSettings.data.standart_non_premium_distance, centerLocation);
		let nonPremiumMarkers = RetailerActionComponent.deleteMarkersOutsideCircle(nonPremiumCircle, allNonPremiumMarkers);

		if (nonPremiumMarkers.length < this.farSettings.data.non_premium_number) {
			nonPremiumCircle = this.createCircle(this.farSettings.data.max_non_premium_distance, centerLocation);
			nonPremiumMarkers = RetailerActionComponent.deleteMarkersOutsideCircle(nonPremiumCircle, allNonPremiumMarkers);
			nonPremiumMarkers = this.filterByProduct(nonPremiumMarkers);
			nonPremiumMarkers.sort(RetailerActionComponent.sortByDistance);
			nonPremiumMarkers = nonPremiumMarkers.splice(0, this.farSettings.data.non_premium_max_additional_search_quantity);
		} else {
			nonPremiumMarkers = this.filterByProduct(nonPremiumMarkers);
			nonPremiumMarkers.sort(RetailerActionComponent.sortByDistance);
			nonPremiumMarkers = nonPremiumMarkers.splice(0, this.farSettings.data.non_premium_number);
		}

		return nonPremiumMarkers;
	}

	findClosestMarkers(center): void {
		RetailerActionComponent.deleteMarkers(this.far.markers);
		this.far.markers = this.getMarkers();
		this.far.center.push(center);

		const closestMarkers = RetailerActionComponent.getDistance(this.far.markers, center.geometry.location);
		const premiumMarkers = closestMarkers.filter((marker) => +marker.premium);
		const markersByDuplicationStatus = +this.farSettings.data.duplicate_premium_retailers
			? closestMarkers
			: closestMarkers.filter((marker) => !+marker.premium);

		if (this.farSettings.hasData) {
			this.getBlindsProductType();

			if (!this.embeddedRetailer) {
				this.far.premiumMarkers = this.getClosestPremiumMarkers(premiumMarkers, center.geometry.location);
				this.far.nonPremiumMarkers = this.getClosestNonPremiumMarkers(markersByDuplicationStatus, center.geometry.location);
			} else {
				this.far.premiumMarkers = this.filterByProduct(premiumMarkers);
				this.far.nonPremiumMarkers = this.filterByProduct(markersByDuplicationStatus);
			}

			if (this.farSettings.data.premium_display) {
				const combinedMarkers = this.far.nonPremiumMarkers.concat(this.far.premiumMarkers);

				this.setMarkers(this.far.map, combinedMarkers);
				this.zoomMap(this.far.map, combinedMarkers);
			} else {
				this.setMarkers(this.far.map, this.far.nonPremiumMarkers);
				this.zoomMap(this.far.map, this.far.nonPremiumMarkers);
			}

			this.sendReporting();
			this.updateRetailers();
			$(SELECTORS.request_button).removeClass(CLASSES.loading);

			if (!this.far.nonPremiumMarkers.length && !this.far.premiumMarkers.length) {
				this.modalService.openResponsiveDialog({
					component: RetailerNoSelectModalComponent,
					minHeight: '200px',
					maxWidth: '680px',
					width: '100%',
					height: 'auto',
				});

				this.searchRetailerAvailability = false;
				this.toggleInfoAvailability.emit(!this.embeddedRetailer);
				return;
			}

			this.shown = !!this.embeddedRetailer;
			this.searchRetailerAvailability = true;
			this.far.hasResults = true;
			this.scrollIntoView();
			setTimeout(this.fitRetailerCardHeight.bind(this));
		}
	}

	sendReporting(): void {
		if (this.embeddedRetailer) {
			return;
		}
		const retailerIds = [...new Set([...this.far.nonPremiumMarkers, ...this.far.premiumMarkers].map((marker) => marker.id))];
		this.overviewReportingService
			.sendReporting({
				retailers: retailerIds,
				origin: 'DYB',
				search: this.contactForm?.post_code || '',
				search_type: 'postcode',
				search_count: this.far.search_count,
				access: this.far.access,
				product_type: this.productType,
			})
			.pipe(
				catchError((error) => {
					console.error('sendReporting failed:', error);
					return EMPTY;
				}),
			)
			.subscribe();
	}

	onSubmit(form: ContactForm): void {
		this.contactForm = form;

		if (this.embeddedRetailer) {
			this.onSendDataByCompanyId(this.far.geocoder);
			return;
		}

		this.contactFormLoading = true;
		this.far.checkedRetailersID = [];
		this.checkedRetailers = [];
		this.far.numberOfSelectedRetailers = 0;
		this.mapSelectedRetailer = null;

		[...this.far.nonPremiumMarkers, ...this.far.premiumMarkers].forEach((retailer: Retailer) => {
			retailer.marker.checked = false;
		});

		delete this.contactForm.confirm_email;

		this.far.formData = this.contactForm;

		this.checkAccessAndProcessSearch();
	}

	onSendDataByCompanyId(geocoder) {
		delete this.contactForm.confirm_email;
		this.far.formData = this.contactForm;

		let addressCode = this.contactForm.post_code;
		const postCodeRegex = /^[0-9]{4}$/;

		if (postCodeRegex.test(addressCode)) {
			addressCode = 'Postcode: ' + addressCode + ' ' + this.countryCodeService.getCurrentCountry()?.name;
		}

		geocoder.geocode(
			{
				address: addressCode,
				componentRestrictions: { country: this.countryCodeService.getCurrentCountry()?.code },
			},
			(results, status) => {
				if (results[0].types.every((type) => type === 'country' || type === 'political')) {
					status = google.maps.GeocoderStatus.ZERO_RESULTS;
				}

				if (status === google.maps.GeocoderStatus.OK || this.embeddedRetailer) {
					this.far.center.splice(0, 1, results[0]);
					this.onSendData();
					return;
				}

				if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
					this.modalService.openResponsiveDialog<boolean>({
						component: RetailerNoSelectModalComponent,
						data: true,
						minHeight: '200px',
						maxWidth: '680px',
						width: '100%',
						height: 'auto',
					});
				}
			},
		);
	}

	mapMarkersHelper(): any {
		for (const nonPremiumMarker of this.far.nonPremiumMarkers) {
			this.retailerPointerHelper(nonPremiumMarker);
		}

		for (const premiumMarker of this.far.premiumMarkers) {
			this.retailerPointerHelper(premiumMarker);
		}
	}

	retailerPointerHelper(retailer: Retailer): void {
		const infoWindowContent = `
            <div class="mc-info-window">
                <div class="mc-info-window-header">${retailer.name}</div>
                <div class="mc-info-window-address">${retailer.full_address}</div>
            </div>`;
		const infoWindow: GoogleMapsInfoWindow = new google.maps.InfoWindow({ content: infoWindowContent });
		const isInfoWindowOpen = (infoWindow: GoogleMapsInfoWindow): boolean => infoWindow?.getMap() !== null;

		this.infoWindows.push(infoWindow);

		retailer.marker.addListener('click', () => {
			if (isInfoWindowOpen(infoWindow)) {
				this.closeAllInfoWindows();
				this.mapSelectedRetailer = null;
			} else {
				this.mapSelectedRetailer = retailer;
				this.closeAllInfoWindows();
				infoWindow.open(this.far.map, retailer.marker);
				const listener = this.far.map.addListener('click', () => {
					this.mapSelectedRetailer = null;
					infoWindow.close();
					listener.remove();
				});
				this.actionTrackingService.logRetailerAction({ action: RetailerClickActions.MAP_ICON, retailer_id: retailer.id, origin: 'DYB' });
			}
		});
	}

	onClickRetailerEvent(retailer: Retailer): void {
		if (
			!this.isRetailerMarkerSelected(retailer.marker) &&
			this.far.numberOfSelectedRetailers < this.farSettings.data.number_of_retailers_can_be_selected
		) {
			retailer.marker.checked = true;
			this.far.checkedRetailersID.push(retailer.id);
			this.checkedRetailers.push(retailer);
			++this.far.numberOfSelectedRetailers;

			this.changeRetailerMapMarker();
			this.actionTrackingService.logRetailerAction({
				action: RetailerClickActions.SELECT_RETAILER,
				origin: 'DYB',
				retailer_id: retailer.id,
			});
		} else if (this.isRetailerMarkerSelected(retailer.marker)) {
			retailer.marker.checked = false;
			--this.far.numberOfSelectedRetailers;

			this.far.checkedRetailersID.splice(this.far.checkedRetailersID.indexOf(retailer.id), 1);
			this.checkedRetailers.splice(this.checkedRetailers.indexOf(retailer), 1);
			this.changeRetailerMapMarker();
		}

		if (this.far.numberOfSelectedRetailers >= this.farSettings.data.number_of_retailers_can_be_selected) {
			this.retailerSendData.nativeElement.scrollIntoView({
				behavior: 'smooth',
			});
		}
	}

	private isRetailerMarkerSelected(marker: Retailer['marker']): boolean {
		return !!marker?.checked === true;
	}

	onHoverRetailerEvent(retailer: Retailer): void {
		if (!!retailer.marker.checked === false && this.far.numberOfSelectedRetailers < 2) {
			retailer.marker.setAnimation(google.maps.Animation.BOUNCE);
			setTimeout(() => {
				retailer.marker.setAnimation(null);
			}, 700);
		}
	}

	toggleShow(): void {
		this.shown = !this.shown;
		this.mapSelectedRetailer = null;
		this.closeAllInfoWindows();
		setTimeout(this.fitRetailerCardHeight.bind(this));
		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.SHOW_MAP);
	}

	getCountryCode(): any {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code);
	}

	onSendData(): void {
		this.contactForm.post_code = this.contactForm.post_code !== '' ? this.contactForm.post_code.match(/\d+/)[0] : '';
		this.far.formData = this.contactForm;

		const sessionStorageBlinds = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		if (this.far.numberOfSelectedRetailers === 0) {
			const currentMarkersCount = this.far.nonPremiumMarkers.length + this.far.premiumMarkers.length;

			this.modalService.openResponsiveDialog<boolean>({
				component: RetailerSelectModalComponent,
				data: currentMarkersCount === 1,
				minHeight: '200px',
				maxWidth: '680px',
				width: '100%',
				height: 'auto',
			});

			return;
		}

		const embedKey = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embed_key);
		const sceneIVScreenshot = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_screenshot);
		const data = {
			action: '3DConfigurator',
			center_latitude: this.far.center[0].geometry.location.lat(),
			center_longitude: this.far.center[0].geometry.location.lng(),
			retailers: this.far.checkedRetailersID,
			country: this.getCountryCode(),
			embed_key: embedKey,
			origin: embedKey ? 'embed' : 'default',
			blinds: [],
			iv_screenshot: sceneIVScreenshot ? sceneIVScreenshot : null,
			time: 0,
			product_type: this.productType,
		};
		for (const blind of sessionStorageBlinds) {
			if (blind.has_name) {
				data.blinds.push(blind);
			}
		}

		this.saveSessionBlindService.updateBlindsStorage(data.blinds);
		const sessionKey = this.saveSessionBlindService.getSessionKeyForSavingHandler();
		this.contactFormLoading = true;
		this.loading = true;
		this.shareServiceData.getUserTime.subscribe((res) => {
			data.time = res;
		});

		this.saveSessionBlindService.preventReloadPage(true);
		this.saveSessionBlindService
			.saveSessionOnAction('send_request', { suppressErrorModal: true })
			.pipe(
				catchError((error) => {
					console.error('Session save failed, continuing with retailer request:', error);
					// TODO: add reporting for failed session save
					return of(null);
				}),
				switchMap(() => this.serverDataService.PostData({ ...data, ...this.far.formData }, sessionKey, { suppressErrorModal: true })),
				catchError((error) => {
					console.error('Failed to send retailer request:', error);
					this.openEnquiryErrorModal(error);
					// TODO: add reporting for failed retailer request
					return EMPTY;
				}),
				finalize(() => {
					this.contactFormLoading = false;
					this.loading = false;
				}),
			)
			.subscribe((res) => {
				this.saveSessionBlindService.sessionStorageSessionKeyHelper();
				this.sessionStorageService.setBlindData(true, STORAGE_NAMES.zip_blind_progress_saved);
				RetailerActionComponent.onConsoleResponse(res.message);
				this.shareServiceData.setRetailerRequest(true);
				this.saveSessionBlindService.preventReloadPage(false);

				this.zone.run(() => {
					this.modalService.openResponsiveDialog<typeof this.far>({
						component: RetailerSuccessModalComponent,
						data: this.far,
						width: '900px',
					});
				});
			});
	}

	private openEnquiryErrorModal(error): void {
		const dialogRef = this.modalService.openResponsiveDialog<{
			error: Error;
		}>({
			id: 'enquiry-error-modal',
			component: EnquiryErrorModalComponent,
			data: { error },
			disableClose: false,
			maxWidth: '80%',
			maxHeight: '100vh',
			panelClass: '',
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === 'retry') {
				this.onSendData();
			}
		});
	}

	onScrollToAddContact(): void {
		if (this.far.numberOfSelectedRetailers === 0) {
			const currentMarkersCount = this.far.nonPremiumMarkers.length + this.far.premiumMarkers.length;

			this.modalService.openResponsiveDialog<boolean>({
				component: RetailerSelectModalComponent,
				data: currentMarkersCount === 1,
				minHeight: '200px',
				maxWidth: '680px',
				width: '100%',
				height: 'auto',
			});

			return;
		}

		this.contactRetailerAvailability = true;

		setTimeout(() => {
			$(SELECTORS.overview_add_block)[0].scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		});
	}

	closeAllInfoWindows(): void {
		this.infoWindows.forEach((infoWindow) => infoWindow.close());
	}

	onRetailerCardHandler(retailer: Retailer): void {
		retailer.open = !retailer.open;

		this.getArrayWithoutDuplicates(this.far.premiumMarkers, this.far.nonPremiumMarkers).map(
			(item: Retailer) => (item.open = item.id !== retailer.id ? false : item.open),
		);

		this.closeAllInfoWindows();
	}

	changeRetailerMapMarker() {
		const retailers = [...this.far.premiumMarkers, ...this.far.nonPremiumMarkers];

		for (const retailer of retailers) {
			const folder = 'assets/icons/new/';
			let icon =
				folder +
				(+retailer.premium
					? retailer.marker.checked
						? 'map-marker-selected-premium.svg'
						: 'map-marker-premium-new.svg'
					: retailer.marker.checked
						? 'map-marker-selected.svg'
						: 'map-marker-new.svg');

			if (this.far.numberOfSelectedRetailers >= 2 && !retailer.marker.checked) {
				icon = folder + (+retailer.premium ? 'map-marker-disabled-premium.svg' : 'map-marker-disabled.svg');
			}

			retailer.marker.setIcon(icon);
		}
	}

	getBlindsProductType(): void {
		const blindsData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const productTypes = blindsData.reduce((acc, blind) => (!acc.includes(blind.type) ? [...acc, blind.type] : acc), []);
		this.productType = productTypes.length === 2 ? 'all' : productTypes[0];
	}

	filterByProduct(array): any {
		return array.filter((item) => item.product === this.productType || item.product === 'all');
	}

	getArrayWithoutDuplicates<T>(...args: T[][]): T[] {
		return [...new Set(args.flat())];
	}

	onLinkHandler() {
		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.PRIVACY_LINK);
	}
}

@Component({
	selector: 'app-search-limit-modal',
	templateUrl: './modals/search-limit-modal.html',
	styleUrls: ['./retailer-action.component.scss', './retailer-action.responsive.scss'],
	standalone: true,
})
export class SearchLimitModalComponent {
	constructor(public dialogRef: MatDialogRef<SearchLimitModalComponent>) {}

	closeDialog(): void {
		this.dialogRef.close();
	}
}
